import { _ftCamField } from '../shared/campaign-field';
import { campaignLightForClock } from '../shared/campaign-light';
// campaign-clock-runtime.ts — the DERIVED campaign clock in the browser (S1 of
// plans/campaign-clock-client-handoff-2026-09-03.md). Member of the post-template-mid aggregate via
// campaign-time-init/index.ts.
//
// Every viewer (GM and players) renders the campaign clock LOCALLY off the campaign blob it already holds:
//     now = checkpoint columns + min(server_now − campaign_clock_running_since, stale cap)
// with server_now = Date.now() − offset captured from computed.campaign.server_utc_now on each blob. A 1 s
// painter writes the three widget spans + the blob's computed calendar_current_*_f / campaign_now_seconds
// (so the activity bars and any later re-render read the derived time), and dispatches
// `ft:campaign-clock-tick`. Zero requests for the time.
//
// The GM tab is the sole clock authority: play / pause POST checkpoint_campaign_time_inc.php; while playing
// it checkpoints every FT_CLOCK_CHECKPOINT_MS (heartbeat=1 replays the out-of-combat heartbeat for the
// rounds that elapsed — the 6 s pushCampaignTime tick is retired in derived mode, see my_campaign.php) and
// on pagehide through the keepalive sender. Players never write. Each checkpoint answers with the landed time
// + new base, which re-bases this tab even when the WS push is late.
//
// Kill switch: computed.campaign.clock_derived === false (env FT_CLOCK_DERIVED=off, or the column missing)
// → this runtime stays inert and my_campaign.php's 6 s tick behaves exactly as before.

import { ftCampaignClockIsRunning } from '../shared/campaign-clock-transport';
import {
    ftClockBaseFromArr,
    ftClockParseUtc,
    ftDeriveCampaignClock,
    type CampaignClockBase,
    type CampaignClockCols,
    type CampaignClockDerived,
} from '../shared/campaign-clock';

const FT_CLOCK_CHECKPOINT_MS = 30_000;
const FT_CLOCK_ENDPOINT = 'options/tools/campaigns/checkpoint_campaign_time_inc.php';

type FateTideClockWindow = Window & {
    ftSendCampaignClock?: (payload: unknown, keepalive?: boolean) => Promise<unknown>;
    BASE_URL?: string;
    FateTide?: {
        campaign?: { id?: string };
        campaignCache?: Map<string, unknown>;
        campaignTransport?: Record<string, { running: number; fast_forward: number }>;
        _myCampaignTimeControlsInCombat?: boolean;
        [k: string]: unknown;
    };
    getCampaignArr?: (campaignId: string, bustCache?: boolean) => Promise<unknown>;
    ftUpdateActivityBars?: (casterId?: string) => void;
    ftRenderCampaignTransportIndicator?: () => void;
    syncMyCampaignTimeSpinnerSlot?: () => void;
    syncMyCampaignPlayPauseStopCluster?: () => void;
    ftGetActiveEncounterId?: () => string;
    broadcastCampaignTransport?: (reason?: string) => void;
    displayWarning?: (msg: string) => void;
    /** websocket bundle (campaign-pause-cue.ts): the GM-pause sound; absent there = silence. */
    ftPlayCampaignPauseCue?: (seed: string) => Promise<void> | void;
};

const W = window as unknown as FateTideClockWindow;

/** computed.campaign.heartbeat — the server-authored "is anything due?" embed (campaign_heartbeat_inc.php). */
interface HeartbeatEmbed {
    built_utc?: string;
    now_seconds?: number;
    post_replay?: boolean;
    timed?: Array<{ id: string; target: string; end: number }>;
    past_due?: number;
    crafting_wip?: boolean;
    hunger?: boolean;
    pc_computed?: Record<string, unknown>;
    pcs?: Record<string, string[]>;
    encounter?: { id: string; in_combat?: boolean; reasons: string[] } | null;
    timed_error?: string;
    build_ms?: number;
}

interface CheckpointResponse {
    ok: boolean;
    recovered?: boolean;
    action?: string;
    derived?: boolean;
    reason?: string;
    running?: boolean;
    running_since?: string | null;
    server_utc_now?: string;
    elapsed_applied?: number;
    heartbeat_rounds?: number;
    heartbeat_performed?: boolean;
    heartbeat?: HeartbeatEmbed;
    time?: CampaignClockCols;
    in_combat?: boolean;
}

// S2 "is anything due?" state (GM tab). A replay is requested the second something is due, never more
// often than FT_CLOCK_DUE_MIN_GAP_MS; the periodic checkpoint carries heartbeat=0 when nothing is.
const FT_CLOCK_DUE_MIN_GAP_MS = 5_000;
let ftClockHeartbeat: HeartbeatEmbed | null | undefined = undefined; // undefined = no embed known → due
let ftClockDirty = false;               // a creature/encounter WS frame arrived since the last replay
let ftClockLastHeartbeatAt = 0;         // Date.now() of the last replay request that was answered
let ftClockSunSignature = '';
let ftClockSunTransitions: number[] | null = null; // computed.campaign.sun_transition_minutes (null = endless)
let ftClockLightMinuteAtReplay: number | null = null;

function ftClockMinuteOfDay(cols: CampaignClockCols): number {
    return Math.max(0, Math.min(23, cols.hour)) * 60 + Math.max(0, Math.min(59, cols.minute));
}

/** Did the clock cross a sun transition since the last replay? (check_campaign_light writes only on a flip.) */
function ftClockLightDue(nowMinute: number): boolean {
    if (!ftClockSunTransitions || ftClockLightMinuteAtReplay === null) return false;
    const from = ftClockLightMinuteAtReplay;
    for (const t of ftClockSunTransitions) {
        if (from <= nowMinute ? (t > from && t <= nowMinute) : (t > from || t <= nowMinute)) return true;
    }
    return false;
}

/** Timed rows this tab already asked a replay for while they were past due (Codex 2026-09-04 #10): a row still
 *  listed AND still past due after that replay is dead to the sweep — never re-fire for it. Pruned when the row
 *  leaves the embed. */
const ftClockTimedAttempted = new Set<string>();
let ftClockLastFrameAt = 0;               // Date.now() of the last creature/encounter WS frame (remembered, never dropped)
let ftClockLastFrameReplayAt = 0;         // when a frame last caused a replay (rate limit)
const FT_CLOCK_FRAME_QUIET_MS = 20_000;   // our own replay's fan-out can trail its HTTP answer by this much under load
const FT_CLOCK_FRAME_REPLAY_MIN_GAP_MS = 30_000; // frame-caused replays are rate-limited; the periodic checkpoint covers the rest

/**
 * The gate, split in two kinds (Codex 2026-09-04 #3):
 *   EVENT reasons — a timed row crossed its end, the sun crossed a transition minute, a state frame arrived —
 *   fire a replay at once (≥ FT_CLOCK_DUE_MIN_GAP_MS apart).
 *   PERSISTENT reasons — regen, class mechanics, encounter per-round effects, an
 *   unknown/missing embed — are true for minutes at a time; they ride the periodic 30 s checkpoint with
 *   heartbeat=1 (rounds are conserved server-side through the carry), never the 5 s due tick.
 * Conservative: anything unknown reads as due (persistent).
 */
function ftClockDueReasonsSplit(d: CampaignClockDerived | null): { event: string[]; persistent: string[] } {
    const derive = (window as unknown as { ftRefreshCreatureHeartbeat?: (input: Record<string, unknown>) => Record<string, unknown> }).ftRefreshCreatureHeartbeat;
    const hb = ftClockHeartbeat && derive ? derive(ftClockHeartbeat as unknown as Record<string, unknown>) as unknown as HeartbeatEmbed : ftClockHeartbeat;
    const persistent: string[] = [];
    const event: string[] = [];
    if (hb === undefined || hb === null) return { event, persistent: ['no_embed'] };
    if (hb.timed_error) persistent.push('timed_error');
    // Hunger (R-123) and crafting (R-083) use the browser observer after each periodic
    // checkpoint, below; neither needs a replay of the retired PHP sweep.
    for (const [pc, rs] of Object.entries(hb.pcs ?? {})) {
        if (rs.length) persistent.push(`pc:${pc.slice(0, 8)}:${rs.join('+')}`);
    }
    if (hb.encounter && hb.encounter.reasons.length) persistent.push(`encounter:${hb.encounter.reasons.join('+')}`);
    if (d) {
        const now = d.formatted.campaign_now_seconds;
        for (const t of hb.timed ?? []) {
            if (t.end <= now && !ftClockTimedAttempted.has(t.id)) { event.push(`timed:${t.id.slice(0, 8)}`); break; }
        }
        if (ftClockLightDue(ftClockMinuteOfDay(d.cols))) event.push('light');
    }
    // A remembered frame counts once it is outside our own replay's quiet window and the rate limit allows.
    if (ftClockDirty
        && Date.now() - ftClockLastHeartbeatAt >= FT_CLOCK_FRAME_QUIET_MS
        && Date.now() - ftClockLastFrameReplayAt >= FT_CLOCK_FRAME_REPLAY_MIN_GAP_MS) {
        event.push('ws_frame');
    }
    return { event, persistent };
}

function ftClockDueReasons(d: CampaignClockDerived | null): string[] {
    const s = ftClockDueReasonsSplit(d);
    return [...s.event, ...s.persistent];
}

/** Bookkeeping when a replay we asked for was answered. */
function ftClockNoteReplayAnswered(d: CampaignClockDerived | null, firedReasons: string[]): void {
    ftClockLastHeartbeatAt = Date.now();
    if (firedReasons.includes('ws_frame')) {
        ftClockLastFrameReplayAt = Date.now();
        ftClockDirty = false;
    } else if (ftClockDirty && ftClockLastFrameAt <= ftClockLastHeartbeatAt) {
        ftClockDirty = false; // the replay covered every frame seen so far
    }
    if (d) ftClockLightMinuteAtReplay = ftClockMinuteOfDay(d.cols);
}

/**
 * Keep the attempted-row memory in step with the embed. A row is forgotten only when it comes back with a
 * FUTURE end (re-timed / re-applied — a fresh due moment to fire for). Mere absence is NOT a reset: a
 * post-replay embed drops dead rows and the next time-only answer lists them again; forgetting on absence
 * made the tab fire for the same dead rows after every periodic checkpoint (S2 review-fix smoke, 2026-09-04).
 */
function ftClockPruneAttempted(): void {
    const now = ftClockDerive()?.formatted.campaign_now_seconds;
    if (now === undefined) return;
    for (const t of ftClockHeartbeat?.timed ?? []) {
        if (t.end > now) ftClockTimedAttempted.delete(t.id);
    }
    if (ftClockTimedAttempted.size > 2000) ftClockTimedAttempted.clear(); // pathological campaign: bounded memory
}

let ftClockBase: CampaignClockBase | null = null;
let ftClockCampaignId = '';
let ftClockServerOffsetMs = 0;
let ftClockOffsetKnown = false;
let ftClockTickTimer: number | null = null;
let ftClockCheckpointTimer: number | null = null;
let ftClockGmEnabled = false;
let ftClockRequestInFlight = false;
let ftClockRequestStartedAt = 0;
/** How long a request of ours may hold a pushed blob off the base before we defer to the push again. */
const FT_CLOCK_INFLIGHT_GUARD_MS = 15_000;
let ftClockLastPaintKey = '';
let ftClockLastMinuteKey = '';
let ftClockLast: CampaignClockDerived | null = null;

function ftClockCid(): string {
    return ftClockCampaignId || String(W.FateTide?.campaign?.id || '').trim();
}

function ftClockServerNowMs(): number {
    return Date.now() - ftClockServerOffsetMs;
}

// ── The PAUSE cue (owner request 2026-09-02, wired 2026-10-12): SoundForge's `time__pause`, played once on the
// running→paused EDGE. Every base change funnels through ftClockNoteRunEdge(), so each tab sees the edge once: on
// the GM tab it is the optimistic pause (the server's answer and the pushed echo then agree: no second edge), on
// every other tab the pushed blob. Boot only RECORDS (a tab opened on a stopped clock is not a pause); a rollback
// only records (an in-combat PLAY bouncing back to stopped is not the GM pausing); the legacy lane
// (FT_CLOCK_DERIVED=off) stays silent. The sound itself is websocket/campaign-pause-cue.ts.
let ftClockSeenRunning: boolean | null = null; // null = not yet observed on this tab
let ftClockSeenSince = '';                     // running_since of the run being watched = the table-wide seed
let ftClockPauseCueAt = 0;
/** One edge, one cue: a late or out-of-order frame flicking the base back and forth must not replay it. */
const FT_CLOCK_PAUSE_CUE_GUARD_MS = 3000;
function ftClockNoteRunEdge(silent = false): void {
    if (!ftClockBase?.derived) { ftClockSeenRunning = null; return; }
    const running = ftClockBase.runningSinceMs !== null;
    const was = ftClockSeenRunning;
    ftClockSeenRunning = running;
    if (running) { ftClockSeenSince = ftClockBase.runningSinceRaw ?? ''; return; }
    if (silent || was !== true) return;
    const now = Date.now();
    if (now - ftClockPauseCueAt < FT_CLOCK_PAUSE_CUE_GUARD_MS) return;
    ftClockPauseCueAt = now;
    void W.ftPlayCampaignPauseCue?.(`${ftClockCid()}|${ftClockSeenSince}`);
}

let ftClockOffsetAnchored = false; // true once measured RTT-anchored from a checkpoint response
let ftClockBaseStampMs: number | null = null; // server_utc_now of the source the current base came from (ordering)

/**
 * Server-clock offset. Three sources, in trust order:
 *   1. a checkpoint RESPONSE, anchored at the request's send time (server_utc_now is stamped at request
 *      start, so offset = t_send − server_now is exact to the uplink latency, however long the request took);
 *      once anchored, nothing else moves it (Codex 2026-09-04 #9: a pushed stamp that arrives late under WS
 *      lag would drag the clock back by the lag);
 *   2. a PUSHED blob (server_utc_now stamped at write time) — players never anchor, so they take the FRESHEST
 *      push: delivery can only be late, so the smallest offset seen is the closest to true;
 *   3. the page-load blob — possibly a Redis cache HIT stamped up to 120 s ago — only while nothing is known.
 */
function ftClockCaptureOffset(serverUtcNow: unknown, localAtMs: number, source: 'response' | 'push' | 'boot'): void {
    const ms = ftClockParseUtc(serverUtcNow);
    if (ms === null) return;
    const candidate = localAtMs - ms;
    if (source === 'response') {
        ftClockServerOffsetMs = candidate;
        ftClockOffsetKnown = true;
        ftClockOffsetAnchored = true;
        return;
    }
    if (ftClockOffsetAnchored) return;
    if (source === 'boot') {
        if (ftClockOffsetKnown) return;
        ftClockServerOffsetMs = candidate;
        ftClockOffsetKnown = true;
        return;
    }
    // push: keep the freshest (smallest) offset; a boot value is replaced outright.
    if (!ftClockOffsetKnown || ftClockOffsetSource === 'boot' || candidate < ftClockServerOffsetMs) {
        ftClockServerOffsetMs = candidate;
    }
    ftClockOffsetKnown = true;
    ftClockOffsetSource = 'push';
}
let ftClockOffsetSource: 'response' | 'push' | 'boot' | null = null;

/** Snapshot the clock inputs off a campaign blob (page-load seed, campaign_update push, or a fetch). */
function ftClockCaptureBlob(campaignArr: unknown, source: 'push' | 'boot' = 'push'): void {
    if (!campaignArr || typeof campaignArr !== 'object') return;
    const cid = ftClockCid();
    const blobCid = String(
        (campaignArr as { campaigns?: { campaign_id?: unknown } }).campaigns?.campaign_id
        ?? (campaignArr as { campaign_id?: unknown[] }).campaign_id?.[0]
        ?? '',
    ).trim();
    if (cid && blobCid && blobCid.toUpperCase() !== cid.toUpperCase()) return;
    if (!ftClockCampaignId && blobCid) ftClockCampaignId = blobCid;

    const computed = (campaignArr as { computed?: { campaign?: Record<string, unknown> } }).computed?.campaign;
    if (source === 'boot') ftClockOffsetSource ??= 'boot';
    ftClockCaptureOffset(computed?.server_utc_now, Date.now(), source);
    // Ordering (Codex 2026-09-04 #8): a delayed time patch must not overwrite a newer checkpoint answer or a
    // newer push. Both stamp server_utc_now at write time; an older stamp is stale — keep the embed, skip the base.
    const stampMs = ftClockParseUtc(computed?.server_utc_now);
    const stale = stampMs !== null && ftClockBaseStampMs !== null && stampMs < ftClockBaseStampMs;
    // A clock write of OURS that is still out is the same case as a queued one (ftClockPendingAction):
    // the blob describes the state before it, and its own answer re-bases us the moment it lands. Without
    // this a campaign_update fanned out by an unrelated concurrent write (the FT3D session-mode POST that
    // rides the very same pause click) re-bases us to RUNNING mid-pause and re-arms the checkpoint.
    // BOUNDED on purpose (Codex review 2026-09-08): a fetch that never settles holds the flag forever,
    // and an unbounded guard would then freeze the base against every push. Past the window we defer to
    // the pushed blob again — a wedged writer must not also cost us the display.
    const ourWriteOut = ftClockRequestInFlight
        && Date.now() - ftClockRequestStartedAt < FT_CLOCK_INFLIGHT_GUARD_MS;
    if (!stale && ftClockPendingAction === null && !ourWriteOut) {
        ftClockBase = ftClockBaseFromArr(campaignArr);
        if (stampMs !== null) ftClockBaseStampMs = stampMs;
    } else if (!ftClockBase) {
        ftClockBase = ftClockBaseFromArr(campaignArr);
    }
    // S2 inputs: a REBUILT blob carries a fresh embed; a time-only patched blob carries the one from the
    // last rebuild (older than what our last checkpoint answered) — keep ours when it is newer.
    const hb = computed?.heartbeat as HeartbeatEmbed | null | undefined;
    if (hb && typeof hb === 'object') {
        const mine = ftClockHeartbeat?.built_utc ?? '';
        if (!mine || String(hb.built_utc ?? '') >= mine) { ftClockHeartbeat = hb; ftClockPruneAttempted(); }
    } else if (ftClockHeartbeat === undefined) {
        ftClockHeartbeat = hb === null ? null : undefined;
    }
    const light = campaignLightForClock(campaignArr as Record<string, unknown>, ftClockBase.cols);
    if (computed) computed.sun_transition_minutes = light.transitions;
    const sun = light.transitions;
    ftClockSunTransitions = Array.isArray(sun) ? sun.map((v) => Number(v)).filter((v) => Number.isFinite(v)) : null;
    if (ftClockLightMinuteAtReplay === null) ftClockLightMinuteAtReplay = ftClockMinuteOfDay(ftClockBase.cols);
    ftClockLastPaintKey = '';
    ftClockPaint(true);
    ftClockSyncGmControls();
    ftClockSyncPlayerTransport();
    ftClockArmTimers();
    ftClockNoteRunEdge();
    if (!stale && ftClockPendingAction === null && !ourWriteOut) window.dispatchEvent(new CustomEvent('ft:campaign-clock-installed', {
        detail: { campaignId: ftClockCid(), cols: { ...ftClockBase.cols } },
    }));
}

/** Re-base from the server's answer to our own checkpoint (works even when the WS push is late/down). */
function ftClockCaptureResponse(resp: CheckpointResponse, sentAtMs: number, heartbeatRequested: boolean, firedReasons: string[]): void {
    if (!ftClockBase || !resp.ok || !resp.time) return;
    // Offset anchored at SEND time: server_utc_now is stamped at request start, so a heartbeat-replaying
    // checkpoint that takes seconds to answer cannot drag the offset (the first derived smoke froze the
    // painter for 9 s that way).
    ftClockCaptureOffset(resp.server_utc_now, sentAtMs, 'response');
    // A play/pause the GM clicked while this request was out is queued behind it and already applied
    // locally — the answer describes the state BEFORE that click, so it must not overwrite the base.
    if (ftClockPendingAction === null) {
        const since = resp.running ? (resp.running_since ?? null) : null;
        ftClockBase = {
            ...ftClockBase,
            cols: { ...resp.time },
            runningSinceRaw: since,
            runningSinceMs: since ? ftClockParseUtc(since) : null,
        };
        const stampMs = ftClockParseUtc(resp.server_utc_now);
        if (stampMs !== null) ftClockBaseStampMs = stampMs;
    }
    // S2: the answer carries a fresh embed; mirror it onto the cached blob for any other reader.
    if (resp.heartbeat && typeof resp.heartbeat === 'object') {
        ftClockHeartbeat = resp.heartbeat;
        ftClockPruneAttempted();
        const cid = ftClockCid();
        const cached = cid ? W.FateTide?.campaignCache?.get(cid) as { computed?: { campaign?: Record<string, unknown> } } | undefined : undefined;
        if (cached && typeof cached === 'object') {
            cached.computed ??= {};
            cached.computed.campaign ??= {};
            cached.computed.campaign.heartbeat = resp.heartbeat;
        }
    }
    ftClockLastPaintKey = '';
    ftClockPaint(true);
    // Only a PERFORMED replay settles the flags (a no-op / in-combat answer performs none — Codex #5).
    const performed = resp.ok === true; // The saved clock and its durable debt settle this scheduling request.
    if (heartbeatRequested && performed) ftClockNoteReplayAnswered(ftClockDerive(), firedReasons);
    ftClockSyncGmControls();
    ftClockSyncPlayerTransport();
    ftClockArmTimers();
    ftClockNoteRunEdge();
    window.dispatchEvent(new CustomEvent('ft:campaign-clock-installed', {
        detail: { campaignId: ftClockCid(), cols: { ...resp.time } },
    }));
}

// ── Checkpoint LEADER (2026-09-27, owner: "fix the load"): every open GM tab used to POST its own 30 s checkpoint and its own
// due-event replay — N GM tabs on one campaign = N campaign-row writes + N campaign_update publishes per interval. Now ONE tab
// per browser per campaign leads: navigator.locks holds the lock for the tab's lifetime and, when the leader closes, the next
// queued tab's callback runs and it takes over. Only the PERIODIC work is gated (the checkpoint cadence + the due replay);
// play / pause / manual pushes are the GM's own clicks and stay on every tab. No Web Locks → every tab leads (as before).
type FtLockManager = { request: (name: string, cb: () => Promise<void>) => Promise<void> };
let ftClockIsLeader = !(typeof navigator !== 'undefined' && (navigator as unknown as { locks?: FtLockManager }).locks);
let ftClockLeaderFor = '';
let ftClockLeaderRelease: (() => void) | null = null;
function ftClockEnsureLeaderElection(): void {
    const cid = ftClockCid();
    const locks = typeof navigator !== 'undefined' ? (navigator as unknown as { locks?: FtLockManager }).locks : undefined;
    if (!cid || !locks || ftClockLeaderFor === cid) return;
    ftClockLeaderRelease?.(); // this tab switched campaigns: hand the old campaign's lead to its next tab
    ftClockLeaderFor = cid;
    ftClockIsLeader = false;
    locks.request(`ft-campaign-clock-checkpoint:${cid}`, () => new Promise<void>((release) => {
        if (ftClockLeaderFor !== cid) { release(); return; }
        ftClockIsLeader = true;
        ftClockLeaderRelease = () => { ftClockIsLeader = false; ftClockLeaderRelease = null; release(); };
    })).catch(() => { if (ftClockLeaderFor === cid) ftClockIsLeader = true; }); // the lock API failed: lead, as before
}

/** GM tab, once a second while playing: replay the heartbeat the moment an EVENT reason is due (rate-limited). */
function ftClockDueTick(): void {
    if (window.ftBaseCommitsPaused?.()) return;
    if (!ftClockGmEnabled || !ftClockBase?.derived || ftClockBase.runningSinceMs === null) return;
    ftClockEnsureLeaderElection();
    if (!ftClockIsLeader) return; // another GM tab of this campaign runs the replay
    if (ftClockRequestInFlight || ftClockPendingAction) return;
    if (Date.now() - ftClockLastHeartbeatAt < FT_CLOCK_DUE_MIN_GAP_MS) return;
    const inFlight = (W.FateTide?.pushTimeInFlight as Record<string, boolean> | undefined)?.[ftClockCid()];
    if (inFlight) return;
    const d = ftClockDerive();
    const split = ftClockDueReasonsSplit(d);
    if (split.event.length === 0) return;
    ftClockLastDueReasons = [...split.event, ...split.persistent];
    // Remember the past-due rows this replay is for: still listed + still past due afterwards = dead row.
    if (d) {
        for (const t of ftClockHeartbeat?.timed ?? []) {
            if (t.end <= d.formatted.campaign_now_seconds) ftClockTimedAttempted.add(t.id);
        }
    }
    void ftClockPost('checkpoint', true, ftClockLastDueReasons);
}
let ftClockLastDueReasons: string[] = [];

function ftClockDerive(): CampaignClockDerived | null {
    if (!ftClockBase) return null;
    return ftDeriveCampaignClock(ftClockBase, ftClockServerNowMs());
}

function ftClockPaint(force: boolean = false): void {
    const d = ftClockDerive();
    if (!d || !ftClockBase?.derived) return;
    ftClockLast = d;
    const c = d.cols;
    const key = `${c.year}-${c.month}-${c.day} ${c.hour}:${c.minute}:${c.second}`;
    if (!force && key === ftClockLastPaintKey) return;
    ftClockLastPaintKey = key;
    const minuteKey = key.slice(0, key.lastIndexOf(':'));
    const minuteChanged = minuteKey !== ftClockLastMinuteKey;
    ftClockLastMinuteKey = minuteKey;

    // Widget spans (PHP partial + renderCampaignTimeWidget both carry these hooks).
    const f = d.formatted;
    const spans: Array<[string, string]> = [
        ['date', f.calendar_current_date_f],
        ['time', f.calendar_current_time_f],
        ['full-date', f.calendar_current_full_date_named_f],
    ];
    for (const [k, v] of spans) {
        document.querySelectorAll<HTMLElement>(`[data-ft-campaign-time-value="${k}"]`).forEach((el) => {
            if (el.textContent !== v) el.textContent = v;
        });
    }

    // The cached blob's COMPUTED display values follow the derived clock, so any later re-render
    // (getCampaignTimeSummary, the FT3D HUD projection, the activity bars) reads the same time this
    // painter shows. The raw checkpoint columns + running_since are left untouched: they are the base.
    const cid = ftClockCid();
    const cached = cid ? W.FateTide?.campaignCache?.get(cid) as { computed?: { campaign?: Record<string, unknown> } } | undefined : undefined;
    if (cached && typeof cached === 'object') {
        const source = cached as Record<string, unknown>;
        const sig = `${c.month}|${_ftCamField(source, 'campaign_light_cycle_mode')}|${ftClockBase.calendar.months.find(m => m.month_order === c.month)?.month_season}`;
        if (sig !== ftClockSunSignature) {
            ftClockSunSignature = sig;
            ftClockSunTransitions = campaignLightForClock(source, c).transitions;
            ((cached.computed ??= {}).campaign ??= {}).sun_transition_minutes = ftClockSunTransitions;
        }
        cached.computed ??= {};
        cached.computed.campaign ??= {};
        const cc = cached.computed.campaign;
        cc.calendar_current_date_f = f.calendar_current_date_f;
        cc.calendar_current_time_f = f.calendar_current_time_f;
        cc.calendar_current_full_date_f = f.calendar_current_full_date_f;
        cc.calendar_current_full_date_named_f = f.calendar_current_full_date_named_f;
        cc.campaign_now_seconds = f.campaign_now_seconds;
    }
    if (d.running && typeof W.ftUpdateActivityBars === 'function') {
        try { W.ftUpdateActivityBars(); } catch (_) { /* display only */ }
    }
    try {
        window.dispatchEvent(new CustomEvent('ft:campaign-clock-tick', {
            detail: { campaignId: cid, running: d.running, cols: c, formatted: f, minuteChanged },
        }));
    } catch (_) { /* display only */ }
}

function ftClockArmTimers(): void {
    const running = !window.ftBaseCommitsPaused?.() && !!ftClockBase?.derived && ftClockBase.runningSinceMs !== null;
    if (running) {
        if (ftClockTickTimer === null) {
            ftClockTickTimer = window.setInterval(() => { ftClockPaint(false); ftClockDueTick(); }, 1000);
        }
        if (ftClockGmEnabled && ftClockCheckpointTimer === null) {
            ftClockEnsureLeaderElection();
            ftClockCheckpointTimer = window.setInterval(() => {
                if (window.ftBaseCommitsPaused?.()) { ftClockArmTimers(); return; }
                if (!ftClockIsLeader) return; // another GM tab of this campaign owns the checkpoint cadence
                // A manual GM push (+N rounds via push_time_inc) folds the pending span itself; a
                // checkpoint racing it would fold the same seconds twice. Yield this tick.
                const inFlight = (W.FateTide?.pushTimeInFlight as Record<string, boolean> | undefined)?.[ftClockCid()];
                if (inFlight) return;
                // S2: time-only unless something is due — persistent reasons (regen, class mechanics…) ride
                // THIS cadence; event reasons normally fired from the due tick already.
                const reasons = ftClockDueReasons(ftClockDerive());
                ftClockLastDueReasons = reasons;
                void ftClockPost('checkpoint', reasons.length > 0, reasons).then(() => {
                    if (window.ftBaseCommitsPaused?.()) return;
                    // R-123: hunger / thirst on the RUNNING clock are decided by this page (the Janus TS rule), after the checkpoint
                    // folded the span — a rounds-0 calendar commit carrying only the hunger family; nothing due = no request.
                    const hb = (W as unknown as { ftTryCommitHungerHeartbeat?: (i: { campaignId: string; encounterId?: string }) => Promise<unknown> }).ftTryCommitHungerHeartbeat;
                    const sidelog = (W as unknown as { getSidelogContext?: () => { encounter_id?: unknown } | null }).getSidelogContext?.();
                    if (typeof hb === 'function') void hb({ campaignId: ftClockCid(), encounterId: String(sidelog?.encounter_id ?? '') || undefined }).catch(() => { /* the next cadence retries */ });
                });
            }, FT_CLOCK_CHECKPOINT_MS);
        }
    } else {
        if (ftClockTickTimer !== null) { window.clearInterval(ftClockTickTimer); ftClockTickTimer = null; }
        if (ftClockCheckpointTimer !== null) { window.clearInterval(ftClockCheckpointTimer); ftClockCheckpointTimer = null; }
    }
}

/** GM tab: mirror the blob's playing state onto the play/pause cluster (the old DOM-local toggle is now truth-driven). */
function ftClockSyncGmControls(): void {
    if (!ftClockGmEnabled || !ftClockBase?.derived) return;
    if (W.FateTide?._myCampaignTimeControlsInCombat === true) return; // combat chrome owns the cluster
    const play = document.getElementById('play_campaign_time');
    const pause = document.getElementById('pause_campaign_time');
    const moving = document.getElementById('div_time_moving');
    if (!play || !pause) return;
    const running = ftClockBase.runningSinceMs !== null;
    play.style.display = running ? 'none' : '';
    pause.style.display = running ? 'block' : 'none';
    if (moving) moving.style.display = running ? 'block' : 'none';
    W.syncMyCampaignTimeSpinnerSlot?.();
    W.syncMyCampaignPlayPauseStopCluster?.();
}

/** Player tab: the blob's running_since is the transport truth now (no 6 s heartbeat frame needed). */
function ftClockSyncPlayerTransport(): void {
    if (ftClockGmEnabled || !ftClockBase?.derived) return;
    const cid = ftClockCid();
    if (!cid) return;
    const ft = W.FateTide;
    if (!ft) return;
    ft.campaignTransport ??= {};
    const prev = ft.campaignTransport[cid];
    const running = ftClockBase.runningSinceMs !== null ? 1 : 0;
    ft.campaignTransport[cid] = { running, fast_forward: prev?.fast_forward ?? 0 };
    if (!prev || prev.running !== running) {
        W.ftRenderCampaignTransportIndicator?.();
    }
}

function ftClockPayload(action: string, heartbeat: boolean, onCheckpoint?: (settle: (roundRecorded: boolean) => void) => void): Record<string, unknown> {
    const d = ftClockDerive();
    const due = (W as unknown as { ftTimeRoundDue?: (input: { campaignId: string; onCheckpoint?: (settle: (roundRecorded: boolean) => void) => void }) => boolean }).ftTimeRoundDue;
    return {
        ...((W as unknown as { ftPlanCampaignClockRequest?: (action: string, cid: string) => Record<string, unknown> }).ftPlanCampaignClockRequest?.(action, ftClockCid()) ?? {}),
        campaign_id: ftClockCid(),
        action,
        heartbeat: heartbeat ? 1 : 0,
        ...(typeof due === 'function' ? { round_pass_due: due({ campaignId: ftClockCid(), onCheckpoint }) ? 1 : 0 } : {}),
        elapsed_seconds: d?.elapsedSeconds ?? 0,
        base_running_since: ftClockBase?.runningSinceRaw ?? '',
    };
}

type FtClockAction = 'play' | 'pause' | 'checkpoint' | 'sync';
let ftClockPendingAction: FtClockAction | null = null;
let ftClockPendingHeartbeat = false;

/**
 * Optimistic local transition for play / pause: the GM sees the clock start or stop the instant they
 * click, and the checkpoint timers arm/clear immediately. The server's answer (or its pushed blob)
 * re-bases with the landed values right after. A pause folds the derived time into the local base so
 * the display holds the exact second it stopped on. Returns the pre-action base for rollback.
 */
function ftClockApplyLocally(action: FtClockAction): CampaignClockBase | null {
    if (!ftClockBase?.derived) return null;
    const before = ftClockBase;
    if (action === 'pause') {
        const d = ftClockDerive();
        ftClockBase = { ...ftClockBase, cols: d ? { ...d.cols } : { ...ftClockBase.cols }, runningSinceRaw: null, runningSinceMs: null };
    } else if (action === 'play' && ftClockBase.runningSinceMs === null) {
        ftClockBase = { ...ftClockBase, runningSinceMs: ftClockServerNowMs() };
    } else {
        return null;
    }
    ftClockLastPaintKey = '';
    ftClockPaint(true);
    ftClockArmTimers();
    ftClockNoteRunEdge();
    return before;
}

/** Roll an optimistic play/pause back to the server's word (or the pre-action base) — Codex 2026-09-04 #7. */
function ftClockRollback(before: CampaignClockBase | null, resp: CheckpointResponse | null): void {
    if (!ftClockBase) return;
    if (resp && typeof resp.running === 'boolean') {
        const since = resp.running ? (resp.running_since ?? null) : null;
        ftClockBase = { ...ftClockBase, runningSinceRaw: since, runningSinceMs: since ? ftClockParseUtc(since) : null };
    } else if (before) {
        ftClockBase = before;
    }
    ftClockLastPaintKey = '';
    ftClockPaint(true);
    ftClockSyncGmControls();
    ftClockArmTimers();
    ftClockNoteRunEdge(true); // a refused/failed click is not a pause
}

async function ftClockPost(action: FtClockAction, heartbeat: boolean, firedReasons: string[] = [], retried = false): Promise<CheckpointResponse | null> {
    if (window.ftBaseCommitsPaused?.()) return null;
    if (!ftClockGmEnabled) return null;
    const cid = ftClockCid();
    if (!cid) return null;
    if (ftClockRequestInFlight) {
        // Do not consume a band hint for a periodic request that will be dropped.
        if (action === 'play' || action === 'pause') {
            ftClockApplyLocally(action);
            ftClockPendingAction = action; ftClockPendingHeartbeat = heartbeat;
        }
        return null;
    }
    // Build the payload BEFORE the optimistic transition so a pause still carries the elapsed span and
    // base the GM actually saw (Codex 2026-09-04 #13).
    let settleRoundHint: ((roundRecorded: boolean) => void) | undefined;
    const secondBefore = ftClockBase?.cols.second ?? 0;
    let payload: Record<string, unknown>;
    try { payload = ftClockPayload(action, heartbeat, settle => { settleRoundHint = settle; }); }
    catch (error) {
        if (action === 'play' || action === 'pause') W.displayWarning?.(error instanceof Error ? error.message : 'calendar-light:calendar-unavailable');
        return null;
    }
    let before: CampaignClockBase | null = null;
    if (action === 'play' || action === 'pause') before = ftClockApplyLocally(action);
    ftClockRequestInFlight = true;
    ftClockRequestStartedAt = Date.now();
    let resp: CheckpointResponse | null = null;
    let retryTransition = false;
    try {
        const sentAtMs = Date.now();
        const send = (W as unknown as { ftSendCampaignClock?: (payload: Record<string, unknown>) => Promise<CheckpointResponse | null> }).ftSendCampaignClock;
        if (!send || !payload.clock_plan) {
            if (action === 'play' || action === 'pause') W.displayWarning?.('calendar-light:clock-plan-unavailable');
            ftClockRollback(before, null);
            return null;
        }
        resp = await send(payload);
        if (resp && resp.ok) {
            ftClockCaptureResponse(resp, sentAtMs, heartbeat, firedReasons);
            retryTransition = !retried && resp.recovered === true && resp.action !== action && (action === 'play' || action === 'pause');
        } else if (resp && resp.derived === false) {
            // Kill switch flipped under us: fall back to the legacy tick (my_campaign.php reads isDerived()).
            if (ftClockBase) ftClockBase = { ...ftClockBase, derived: false, runningSinceMs: null };
            ftClockArmTimers();
            ftClockNoteRunEdge();
        } else if (resp && resp.reason === 'in_combat') {
            ftClockRollback(before, resp);
            W.displayWarning?.('Campaign time cannot run while the loaded encounter is in combat.');
        } else if (resp?.reason === 'stale') {
            ftClockRollback(before, null);
            retryTransition = !retried && (action === 'play' || action === 'pause');
        } else if (before) {
            ftClockRollback(before, null);
        }
        return resp;
    } catch (_) {
        if (before) ftClockRollback(before, null); // network failure: the server never heard the click
        return null;
    } finally {
        settleRoundHint?.(resp?.ok === true && resp.recovered !== true && Math.floor((secondBefore % 6 + Number(resp.elapsed_applied ?? 0)) / 6) > 0);
        ftClockRequestInFlight = false;
        const pending = ftClockPendingAction;
        const pendingHb = ftClockPendingHeartbeat;
        ftClockPendingAction = null;
        ftClockPendingHeartbeat = false;
        if (retryTransition && !pending) return await ftClockPost(action, heartbeat, firedReasons, true);
        if (pending) void ftClockPost(pending, pending === 'play' || pendingHb || ftClockDueReasons(ftClockDerive()).length > 0);
    }
}

async function ftClockBeacon(action: 'checkpoint' | 'pause'): Promise<void> {
    if (window.ftBaseCommitsPaused?.()) return;
    if (!ftClockGmEnabled || !ftClockBase?.derived || ftClockBase.runningSinceMs === null) return;
    try {
        const payload = ftClockPayload(action, false);
        if (!payload.clock_plan) return;
        await W.ftSendCampaignClock?.(payload, true);
    } catch (_) { /* best effort */ }
}

const ftCampaignClock = {
    /** True when the blob says the derived clock is live — my_campaign.php skips the legacy 6 s tick then. */
    isDerived(): boolean { return !!ftClockBase?.derived; },
    isRunning(): boolean { return !!ftClockBase?.derived && ftClockBase.runningSinceMs !== null; },
    /** GM page only (my_campaign.php under can-manage): arms checkpoints + the play/pause mirror. */
    enableGm(): void {
        ftClockGmEnabled = true;
        ftClockSyncGmControls();
        ftClockArmTimers();
        window.addEventListener('pagehide', () => ftClockBeacon('checkpoint'));
    },
    /** Play replays the heartbeat once: anything a heartbeat-less clock edit left past due is settled here. */
    play(): Promise<CheckpointResponse | null> { return ftClockPost('play', true); },
    pause(): Promise<CheckpointResponse | null> { return ftClockPost('pause', ftClockDueReasons(ftClockDerive()).length > 0); },
    checkpoint(): Promise<CheckpointResponse | null> { return ftClockPost('checkpoint', ftClockDueReasons(ftClockDerive()).length > 0); },
    captureBlob(campaignArr: unknown): void { ftClockCaptureBlob(campaignArr, 'push'); },
    /** A FRESH derivation (the painter's last value can be a few seconds stale on a starved tab). */
    getDerived(): CampaignClockDerived | null { return ftClockDerive() ?? ftClockLast; },
    serverNowMs(): number { return ftClockServerNowMs(); },
    serverOffsetKnown(): boolean { return ftClockOffsetKnown; },
    /** S2 inspection: the current due reasons (empty = idle), the embed, and the last reasons that fired. */
    getDue(): { reasons: string[]; heartbeat: HeartbeatEmbed | null | undefined; lastFired: string[] } {
        return { reasons: ftClockDueReasons(ftClockDerive()), heartbeat: ftClockHeartbeat, lastFired: ftClockLastDueReasons };
    },
};

// S2: a creature / encounter state frame (router.ts) may carry a status change the embed predates. The
// evidence is REMEMBERED, never dropped (Codex 2026-09-04 #6): the due gate acts on it once our own replay's
// quiet window and the rate limit allow, and a performed replay that post-dates the frame clears it.
window.addEventListener('ft:ws-state-frame', () => {
    if (!ftClockGmEnabled || !ftClockBase?.derived) return;
    ftClockLastFrameAt = Date.now();
    if (ftClockRequestInFlight) return; // our own replay's fan-out while it is still answering
    // Our own replay's fan-out can also land AFTER its answer (WS lag of 10 s+ seen on the loaded box). A frame
    // inside the quiet window is treated as that echo and dropped — a real player action in those 20 s is not
    // lost, only late: the next periodic checkpoint (≤ 30 s) rebuilds the embed from the blobs PAI just wrote.
    if (Date.now() - ftClockLastHeartbeatAt < FT_CLOCK_FRAME_QUIET_MS) return;
    ftClockDirty = true;
});

declare global {
    interface Window {
        ftCampaignClock: typeof ftCampaignClock;
        /** Bridge for my_campaign.php's INLINE JS, which cannot import the shared reader. */
        ftCampaignClockIsRunning: typeof ftCampaignClockIsRunning;
    }
}
window.ftCampaignClock = ftCampaignClock;
// my_campaign.php's inline transport mirror asks this instead of reading #pause_campaign_time's display
// string, which is 'none' for BOTH buttons in in-combat chrome while the clock may still be running.
window.ftCampaignClockIsRunning = ftCampaignClockIsRunning;
window.addEventListener('ft:unported', event => {
    if ((event as CustomEvent).detail?.reason === 'engine-version-reload') ftClockArmTimers();
});

// Pushed blobs re-base the clock (the same event the attendance slice listens to).
window.addEventListener('ft:campaign-state-received', (event) => {
    const detail = (event as CustomEvent<{ campaignId?: string; campaignArr?: unknown }>).detail;
    if (detail?.campaignArr) ftClockCaptureBlob(detail.campaignArr, 'push');
});

// Page-load seed: the cache-first campaign fetch (a hit costs nothing; a miss is the one request the page
// already makes). Runs after DOM ready so window.FateTide.campaign.id is set by template.js.
function ftClockBoot(): void {
    const cid = ftClockCid();
    if (!cid || typeof W.getCampaignArr !== 'function') return;
    // Only pages that show a clock (the widget spans) or already hold the blob: never ADD a campaign
    // fetch to a page that had none.
    const cached = W.FateTide?.campaignCache?.get(cid);
    if (!cached && !document.querySelector('[data-ft-campaign-time-value]')) return;
    void W.getCampaignArr(cid).then((arr) => { if (arr) ftClockCaptureBlob(arr, 'boot'); }).catch(() => { /* seed is best effort */ });
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.setTimeout(ftClockBoot, 0), { once: true });
} else {
    window.setTimeout(ftClockBoot, 0);
}

export {};
